import {html, nothing} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {ifDefined} from "lit/directives/if-defined.js";
import {createRef, ref} from "lit/directives/ref.js";
import {when} from "lit/directives/when.js";
import {UuidHelper} from "../../../helpers/uuid.helper";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";

@customElement(`ha-input-text`)
export class HaInputTextComponent extends AbstractLitInputComponent<string> {

    @property({type: Boolean, attribute: `disable-autocomplete`})
    public disableAutocomplete: boolean = false

    @property({type: Boolean})
    public obscure: boolean = false;

    @property({type: Boolean, attribute: `with-clear-button`})
    public withClearButton: boolean = false;

    @property({type: Number})
    public maxlength?: number;

    /**
     * Hint text shown while the field is empty.
     *
     * Declared explicitly because this component renders into the LIGHT DOM: an undeclared
     * `placeholder` attribute in markup just sits on the host element and never reaches the
     * inner <input> — silently, with no error. Two call sites had been setting it for months
     * with no effect.
     */
    @property({type: String})
    public placeholder?: string;

    private _id = UuidHelper.generateV7();

    private _inputRef = createRef<HTMLInputElement>();

    public focus(options?: FocusOptions): void {
        if (this._inputRef.value instanceof HTMLInputElement) {
            this._inputRef.value.focus(options);
        }
    }

    protected _onInput(evt: Event): void {
        if (this.readonly) {
            return;
        }

        const str: string = (evt.target as HTMLInputElement).value.trim();
        if (str !== this.value) {
            this.value = str;
            this.dispatchEvent(
                new CustomEvent<{ value: string }>(`value-changed`, {detail: {value: this.value!}})
            );
        }
    }

    protected _onKeyUp(evt: KeyboardEvent): void {
        if (this.readonly) {
            return;
        }

        if (this.obscure && evt.key === `Enter`) {
            this.dispatchEvent(new CustomEvent(`pressed-enter`));
        }
    }

    protected render(): unknown {
        return html`
            ${when(!!this.label, () => html`
                    <label for=${this._id}>${this.label}</label>
            `)}
            <div class="wrapper">                
                <input type=${this.obscure ? `password` : `text`}
                       id=${this._id}
                       .value=${this.value}
                       @input=${this._onInput}
                       @keyup=${this._onKeyUp}
                       ?disabled=${this.readonly}
                       maxlength=${ifDefined(this.maxlength)}
                       placeholder=${ifDefined(this.placeholder)}
                       autocomplete=${ifDefined(this.disableAutocomplete ? `new-password` : nothing)}
                       ${ref(this._inputRef)}
                >
                ${when(this.withClearButton, () => html`
                    <i class="clear-button" @click=${this._onClickClearButton}></i>
                `)}
            </div>    
            ${this._renderHelperText()}
            ${this._renderErrors()}
        `;
    }

    private _onClickClearButton(): void {
        if (this.readonly) {
            return;
        }

        this._inputRef.value!.value = '';
        const evt = new Event(`input`, {bubbles: true, cancelable: true});
        this._inputRef.value!.dispatchEvent(evt);
    }
}